<template>
  <div class="gw-detail" :class="`gw-detail--${layout}`">
    <div v-if="layout === 'row'" class="gw-detail__media">
      <img
        v-if="item.preview && !imageFailed"
        :src="item.preview"
        alt=""
        class="gw-detail__img"
        @error="imageFailed = true"
      />
      <div v-else class="gw-detail__placeholder">
        {{ initial }}
      </div>
    </div>

    <div class="gw-detail__body">
      <!-- Pour une source vidéo, la « description » est la légende du flux
           RSS : elle ne dit rien de ce qui est dit dans la vidéo. On l'annonce
           comme telle plutôt que de la présenter comme le contenu. -->
      <div class="gw-detail__block">
        <div class="gw-detail__label">
          {{ item.isVideo ? "Légende de la vidéo" : "Meta description" }}
        </div>
        <p class="gw-detail__text">
          {{ item.description || "Aucune description fournie par la source." }}
        </p>
      </div>

      <!-- scores.notes is returned by the API but was never displayed before. -->
      <div v-if="item.notes" class="gw-detail__block">
        <div class="gw-detail__label">Pourquoi ce score</div>
        <p class="gw-detail__text gw-detail__text--muted">{{ item.notes }}</p>
      </div>

      <div class="gw-detail__meta">
        <div class="gw-detail__meta-item">
          <div class="gw-detail__label gw-detail__label--sm">Publié le</div>
          <div class="gw-detail__meta-value">{{ fullDate }}</div>
        </div>
        <div class="gw-detail__meta-item">
          <div class="gw-detail__label gw-detail__label--sm">Origine</div>
          <div class="gw-detail__meta-value">{{ item.originLabel }}</div>
        </div>
        <div v-if="item.domain" class="gw-detail__meta-item">
          <div class="gw-detail__label gw-detail__label--sm">Domaine</div>
          <div class="gw-detail__meta-value">{{ item.domain }}</div>
        </div>
      </div>

      <div class="gw-detail__footer">
        <a
          v-if="item.url"
          :href="item.url"
          target="_blank"
          rel="noopener"
          class="gw-detail__link"
        >
          {{ item.isVideo ? "Ouvrir la vidéo" : "Ouvrir l'article" }}
          <v-icon size="13" color="currentColor">
            {{ icons.mdiOpenInNew }}
          </v-icon>
        </a>

        <!-- Le dépliage : rien n'est transcrit tant qu'on n'a pas ouvert UNE
             vidéo (arbitrage C). -->
        <rmr-action-button
          v-if="item.isVideo"
          label="Proposer des angles"
          :icon="icons.mdiCreation"
          variant="primary"
          size="sm"
          @click="$emit('open-video', item)"
        />
      </div>
    </div>
  </div>
</template>

<script>
import { computed, ref } from "@vue/composition-api";
import { mdiCreation, mdiOpenInNew } from "@mdi/js";

import RmrActionButton from "@/components/ui/actions/RmrActionButton.vue";
import { formatFullDate } from "../source-presentation";

export default {
  name: "SourceDetailPanel",
  components: {
    RmrActionButton,
  },
  props: {
    item: {
      type: Object,
      required: true,
    },
    layout: {
      type: String,
      default: "row",
      validator: (value) => ["row", "card"].includes(value),
    },
  },
  setup(props) {
    const imageFailed = ref(false);

    return {
      icons: { mdiCreation, mdiOpenInNew },
      imageFailed,
      fullDate: computed(() => formatFullDate(props.item.publicationDate)),
      initial: computed(() =>
        (props.item.domain || props.item.title || "?").charAt(0).toUpperCase()
      ),
    };
  },
};
</script>

<style lang="scss" scoped>
@import "../_global-watch.scss";

.gw-detail {
  background: $rmr-surface-2;
  border: 1px solid $rmr-border;
  border-radius: $rmr-radius-xl;
  overflow: hidden;
}

.gw-detail--row {
  display: grid;
  grid-template-columns: 220px 1fr;
  max-width: 720px;
}

.gw-detail--card {
  background: transparent;
  border: none;
  border-top: 1px solid $rmr-border;
  border-radius: 0;
  padding-top: 11px;
  margin-top: 12px;
}

.gw-detail__media {
  min-height: 150px;
}

.gw-detail__img {
  display: block;
  width: 100%;
  height: 100%;
  min-height: 150px;
  object-fit: cover;
}

.gw-detail__placeholder {
  height: 100%;
  min-height: 150px;
  font-size: 32px;
  @include gw-thumb-placeholder;
}

.gw-detail__body {
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 14px 18px;
  min-width: 0;
}

.gw-detail--card .gw-detail__body {
  padding: 0;
  gap: 10px;
}

.gw-detail__label {
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.6px;
  text-transform: uppercase;
  color: $rmr-text-primary;
  margin-bottom: 4px;
}

.gw-detail__label--sm {
  font-size: 10px;
  color: $rmr-text-subtle;
}

.gw-detail__text {
  margin: 0;
  font-size: 13.5px;
  line-height: 1.6;
  color: $rmr-text-base;
}

.gw-detail__text--muted {
  font-size: 12.5px;
  color: $rmr-text-muted;
}

.gw-detail--card .gw-detail__text {
  font-size: 12px;
  line-height: 1.55;
}

.gw-detail__meta {
  display: flex;
  flex-wrap: wrap;
  gap: 12px 32px;
  padding-top: 10px;
  border-top: 1px solid $rmr-border;
}

.gw-detail__meta-value {
  font-size: 12.5px;
  font-weight: 600;
  color: $rmr-text-base;
}

.gw-detail__footer {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 12px;
}

.gw-detail__link {
  align-self: flex-start;
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 6px 14px;
  border: 1px solid $rmr-indigo;
  border-radius: $rmr-radius-pill;
  color: $rmr-indigo;
  font-size: 12px;
  font-weight: 700;
  text-decoration: none;

  &:hover {
    background: $rmr-indigo-bg;
  }
}

@media (max-width: 760px) {
  .gw-detail--row {
    grid-template-columns: 1fr;
  }
}
</style>
